"use client";
import React, { useState, useMemo, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import HeadingSection from "../others/HeadingSection";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import { useIsMobile } from "@/utils/useIsMobile";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import { useDispatch } from "react-redux";
import { openInvestmentModal } from "@/redux/features/InvestmentModal/investmentModalSlice";
import Icon from "@/components/icons/Icons";
import { ROOT_URL } from "@/utils/gloableVariables";

type FundItem = {
  id?: number;
  scheme_name?: string;
  scheme_code?: string;
  amc_code?: string;
  amc?: string;
  url?: string;
  category_type?: string;
  asset_id?: number;
  sip_allowed?: number;
  lump_allowed?: number;
  minimum_sip_invest?: number;
  minimum_investment?: number;
  six_month_abs?: number;
  one_year_abs?: number;
  three_year?: number;
};

export default function GoldFundsListSection({
  schemeData,
}: {
  schemeData?: FundItem[];
}) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [search, setSearch] = useState("");
  const swiperRef = useRef<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [filteredItems, setFilteredItems] = useState(0);

  const dispatch = useDispatch();

  const isMob = useIsMobile();

  useEffect(() => {
    if (!isMob) return;

    if (isFilterOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  }, [isFilterOpen]);

  // real applied filters
  const [selectedAMCs, setSelectedAMCs] = useState<string[]>([]);
  const [selectedAmount, setSelectedAmount] = useState<string | null>(null);

  // temporary filters inside drawer
  const [tempSelectedAMCs, setTempSelectedAMCs] = useState<string[]>([]);
  const [tempSelectedAmount, setTempSelectedAmount] = useState<string | null>(
    null,
  );

  const filteredSchemes = useMemo(() => {
    if (!schemeData) return [];

    return schemeData.filter(
      (fund) => fund.sip_allowed === 1 || fund.lump_allowed === 1,
    );
  }, [schemeData]);

  const amcList = useMemo(() => {
    const map = new Map();
    filteredSchemes.forEach((item) => {
      if (item.amc && item.amc_code) {
        map.set(item.amc_code, item.amc);
      }
    });
    return Array.from(map, ([amc_code, amc]) => ({ amc_code, amc }));
  }, [filteredSchemes]);

  const filteredData = useMemo(() => {
    let data = [...filteredSchemes];

    if (selectedAMCs.length > 0) {
      data = data.filter((item) => selectedAMCs.includes(item.amc_code!));
    }

    if (selectedAmount) {
      const [min, max] = selectedAmount.split("-").map(Number);

      data = data.filter((item) => {
        const lump = item.minimum_investment ?? 0;
        const sip = item.minimum_sip_invest ?? 0;

        // TRUE if lump or sip is in the selected range
        const lumpInRange = lump >= min && lump <= max;
        const sipInRange = sip >= min && sip <= max;

        return lumpInRange || sipInRange;
      });
    }

    if (search.trim() !== "") {
      data = data.filter((item) =>
        item.scheme_name?.toLowerCase().includes(search.toLowerCase()),
      );
    }

    return data;
  }, [filteredSchemes, selectedAMCs, selectedAmount, search]);

  const totalCols = Math.ceil(filteredData.length / 2);

  if (!schemeData) return null;

  const resetFilters = () => {
    setTempSelectedAMCs([]);
    setTempSelectedAmount(null);

    setSelectedAMCs([]);
    setSelectedAmount(null);
    setSearch("");
    setFilteredItems(0);
  };

  const openFilterDrawer = () => {
    setTempSelectedAMCs(selectedAMCs);
    setTempSelectedAmount(selectedAmount);
    setIsFilterOpen(true);
  };

  const applyFilters = () => {
    setSelectedAMCs(tempSelectedAMCs);
    setSelectedAmount(tempSelectedAmount);
    setSearch("");
    setIsFilterOpen(false);

    const selectedAMCCount = tempSelectedAMCs.length;
    const selectedAmountCount = tempSelectedAmount ? 1 : 0;

    const totalSelected = selectedAMCCount + selectedAmountCount;
    setFilteredItems(totalSelected);
  };

  return (
    <>
      <HeadingSection
        heading="Want to Invest Directly in Our Gold Mutual Funds?"
        subHeading="See our top-performing gold mutual funds and choose one for your lasting financial growth."
      />

      <div className="relative rounded-6xl overflow-hidden">
        <div
          className={`relative w-full bg-transparent rounded-6xl sm:border border-white/80 sm:h-600 ${isFilterOpen ? "overflow-y-hidden" : "overflow-y-auto"} scrollbar-none`}
        >
          <div className="sm:sticky z-1 top-0 px-6 py-4 bg-black flex items-center justify-between gap-3">
            {/* 🔍 Search Input */}
            <label className="group w-full px-4 py-2 max-w-lg rounded-xl bg-transparent transition border border-[#b9b9b9] placeholder-white/40 hover:border-white outline-hidden relative">
              <Icon.searchIcon className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition text-[#b9b9b9] group-hover:text-white" />
              <input
                type="text"
                placeholder="Search Funds..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-transparent px-6 w-full transition text-[#b9b9b9] group-hover:text-white"
              />
            </label>

            {/* FILTER BUTTON */}
            <button
              onClick={openFilterDrawer}
              aria-label="Filter Drawer Open Button"
              className="group relative px-10 py-2 flex items-center gap-3 border border-[#b9b9b9] hover:border-white rounded-xl transition text-[#b9b9b9] hover:text-white"
            >
              <Icon.filterBtnIcon className="transition text-[#b9b9b9] group-hover:text-white" />
              Filter
              <span className="bg-[#c00000] text-white rounded-full flex items-center justify-center w-4.5 h-4.5 text-xs leading-1 absolute -top-1.5 -right-1.5">
                {filteredItems}
              </span>
            </button>
          </div>

          {/* Header */}
          <div className="sm:sticky z-1 top-18.25 hidden sm:grid grid-cols-6 bg-[#121212] text-sm md:text-base lg:text-xl font-semibold px-6 py-4 text-white">
            <div className="col-span-2">FUNDS</div>
            <div className="text-center">RETURNS (6M)</div>
            <div className="text-center">RETURNS (1Y)</div>
            <div className="text-center">RETURNS (3Y)</div>
            <div />
          </div>

          {/* Rows with smooth animation */}
          {!isMob && (
            <div className="hidden sm:flex flex-col transition-all duration-500 ease-in-out">
              {filteredData.length === 0 && (
                <div className="text-center text-white py-10 text-base sm:text-lg">
                  No Gold Fund Found With This Name.
                  <br />
                  Try searching something else.
                </div>
              )}

              {filteredData.map((item) => {
                const schemeDataItems = {
                  scheme_code: item.scheme_code,
                  scheme_name: item.scheme_name,
                  amc_code: item.amc_code,
                  sip_allowed: item.sip_allowed,
                  lump_allowed: item.lump_allowed,
                  minimum_sip_invest: item.minimum_sip_invest,
                  minimum_investment: item.minimum_investment,
                };

                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-6 px-6 py-5 items-center text-white"
                  >
                    <p className="text-sm lg:text-base font-normal col-span-2 line-clamp-2">
                      <Link
                        href={`/mutual-fund/${item.url}/mso${item.id}`}
                        title={item.scheme_name}
                        className="w-11/12 flex items-center gap-3 transition duration-300 text-white hover:text-[#C89900]"
                      >
                        <Image
                          src={`${ROOT_URL}img/AMCsLogo/${item.amc_code}.png`}
                          alt={item?.scheme_name || "Fund Icon"}
                          width={28}
                          height={28}
                          className="w-6 h-6 lg:w-7 lg:h-7 overflow-hidden shrink-0 border border-[#E9E9EB] rounded-full"
                        />

                        <span className="line-clamp-2">{item.scheme_name}</span>
                      </Link>
                    </p>

                    {[
                      item.six_month_abs,
                      item.one_year_abs,
                      item.three_year,
                    ].map((value, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-center space-x-2 text-base lg:text-lg font-medium"
                      >
                        {value == null ? (
                          <span className="text-white">-</span>
                        ) : (
                          <>
                            {value !== 0 && (
                              <Icon.growthSVG
                                className={`w-5 h-5 lg:w-6 lg:h-6 ${value > 0 ? "text-secondaryGreen" : "text-red-500 -scale-y-100"}`}
                              />
                            )}
                            <span
                              className={
                                value > 0
                                  ? "text-secondaryGreen"
                                  : value === 0
                                    ? "text-white"
                                    : "text-red-500"
                              }
                            >
                              {value > 0
                                ? `+${value}%`
                                : value === 0
                                  ? value
                                  : `${value}%`}
                            </span>
                          </>
                        )}
                      </div>
                    ))}

                    <button
                      onClick={() =>
                        dispatch(openInvestmentModal(schemeDataItems))
                      }
                      aria-label="Click To Open Gold Invest Modal"
                      className="w-fit ml-auto text-sm lg:text-base font-semibold  px-6 py-1.5 rounded-full text-white transition duration-300 border border-[#C89900] bg-transparent hover:bg-[#C89900]"
                    >
                      Invest
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Mobile Part Cards */}
          {isMob && (
            <>
              <div className="sm:hidden w-full relative h-66.5">
                {filteredData.length === 0 ? (
                  <div className="text-center text-primaryBlack text-lg font-semibold py-20">
                    No Funds Found
                  </div>
                ) : (
                  <>
                    <Swiper
                      modules={[Navigation]}
                      spaceBetween={0}
                      onSwiper={(s) => (swiperRef.current = s)}
                      onSlideChange={(s) => setActiveIndex(s.activeIndex)}
                      speed={500}
                      slidesPerView="auto"
                      allowTouchMove={true}
                      className="pb-12"
                    >
                      {Array.from({ length: totalCols }).map((_, colIndex) => {
                        const card1 = filteredData[colIndex * 2];
                        const card2 = filteredData[colIndex * 2 + 1];
                        const isLastColumn = colIndex === totalCols - 1;
                        const columnPadding = isLastColumn
                          ? "px-5 xs:px-6"
                          : "pl-5 xs:pl-6";

                        return (
                          <SwiperSlide key={colIndex} className="max-w-fit">
                            <div
                              className={`flex flex-col w-fit gap-4 py-2 ${columnPadding}`}
                            >
                              {card1 && <FundCardComponent fund={card1} />}
                              {card2 && <FundCardComponent fund={card2} />}
                            </div>
                          </SwiperSlide>
                        );
                      })}
                    </Swiper>
                  </>
                )}
              </div>

              <div className="sm:hidden flex items-center justify-center gap-2 mt-10">
                {/* LEFT */}
                <button
                  onClick={() => swiperRef.current?.slidePrev()}
                  disabled={activeIndex === 0}
                  aria-label="Previous Button"
                  className={`group border border-white rounded-full w-8 h-8 sm:w-7 sm:h-7 flex items-center justify-center transition duration-300
                    ${activeIndex === 0 ? "opacity-50 cursor-not-allowed" : "opacity-100 hover:border-[#C89900]"}`}
                >
                  <Icon.closeSearchIcon className="w-4 h-4 transition duration-300 text-white group-hover:text-[#C89900] rotate-270" />
                </button>

                {/* RIGHT */}
                <button
                  onClick={() => swiperRef.current?.slideNext()}
                  disabled={activeIndex >= totalCols - 2}
                  aria-label="Next Button"
                  className={`group border border-white rounded-full w-8 h-8 sm:w-7 sm:h-7 flex items-center justify-center transition duration-300
                    ${activeIndex >= totalCols - 2 ? "opacity-50 cursor-not-allowed" : "opacity-100 hover:border-[#C89900]"}`}
                >
                  <Icon.closeSearchIcon className="mt-px w-4 h-4 transition duration-300 text-white group-hover:text-[#C89900] rotate-90" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Filter List */}
        <AnimatePresence mode="wait">
          {isFilterOpen && (
            <>
              {/* Background overlay */}
              <motion.div
                className="fixed sm:absolute inset-0 bg-white/10 backdrop-blur-[2px] shadow-[-6px_0px_20px_0px_#ffffff30]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ ease: "easeInOut", duration: 0.35 }}
                onClick={() => setIsFilterOpen(false)}
              />

              {/* Drawer Panel */}
              <motion.div
                initial={{ x: "100%", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100%", opacity: 0 }}
                transition={{ ease: "easeInOut", duration: 0.3 }}
                className="fixed sm:absolute inset-y-0 right-0 h-full w-full max-w-lg bg-[#0D0D0D] border border-white/20 p-5 z-5050 sm:z-20 shadow-xl sm:rounded-tr-6xl sm:rounded-br-6xl overflow-hidden"
              >
                <div className="absolute inset-x-0 top-0 p-5 bg-[#0d0d0d] flex justify-between items-center">
                  <p className="text-white sm:text-lg font-semibold">Filters</p>

                  <button
                    onClick={() => setIsFilterOpen(false)}
                    aria-label="Close Popup"
                    className="group w-5 sm:w-6 h-5 sm:h-6 mx53 flex items-center justify-center"
                  >
                    <Icon.popupCloseIcon className="w-full h-full text-white rotate-45 transition hover:text-[#C89900]" />
                  </button>
                </div>

                <div className="max-h-full pt-12 pb-28 text-white overflow-y-auto scrollbar-md pr-2">
                  {/* ⭐ AMC LIST */}
                  <p className="text-sm sm:text-base font-semibold mb-3">
                    AMC List
                  </p>

                  <div className="flex flex-wrap gap-3 mb-8">
                    {amcList.map(({ amc, amc_code }) => (
                      <label
                        key={amc_code}
                        className={`flex items-center gap-2 px-2 py-1 sm:px-3 sm:py-1.5 border rounded-lg text-xs sm:text-sm transition duration-300 cursor-pointer ${selectedAMCs.includes(amc_code) ? "border-[#C89900] bg-[#C89900]/20" : "border-white/20 hover:border-[#C89900]"}`}
                      >
                        <input
                          type="checkbox"
                          checked={tempSelectedAMCs.includes(amc_code)}
                          className="accent-[#C89900] w-3 sm:w-4"
                          onChange={() => {
                            setTempSelectedAMCs((prev) =>
                              prev.includes(amc_code)
                                ? prev.filter((c) => c !== amc_code)
                                : [...prev, amc_code],
                            );
                          }}
                        />
                        {amc}
                      </label>
                    ))}
                  </div>

                  {/* ⭐ MINIMUM INVESTMENT (RADIO - SINGLE SELECT) */}
                  <p className="text-sm sm:text-base font-semibold mb-3">
                    Minimum Investment
                  </p>

                  <div className="flex flex-wrap gap-3">
                    {[
                      { label: "₹0 – ₹500", value: "0-500" },
                      { label: "₹500 – ₹1000", value: "500-1000" },
                      { label: "₹1000 – ₹5000", value: "1000-5000" },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-2 py-1 sm:px-3 sm:py-1.5 border rounded-lg text-xs sm:text-sm transition duration-300 cursor-pointer ${selectedAmount === opt.value ? "border-[#C89900] bg-[#C89900]/20" : "border-white/20 hover:border-[#C89900]"}`}
                      >
                        <input
                          type="radio"
                          name="minInvest"
                          className="accent-[#C89900] w-3 sm:w-4"
                          checked={tempSelectedAmount === opt.value}
                          onChange={() => setTempSelectedAmount(opt.value)}
                        />{" "}
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="absolute inset-x-0 bottom-0 py-4 px-10 bg-[#0d0d0d] flex items-center gap-5">
                  <button
                    onClick={resetFilters}
                    className="w-full py-2 rounded-xl border border-white/30 text-white transition font-medium hover:bg-white/20"
                  >
                    Reset Filters
                  </button>

                  <button
                    onClick={applyFilters}
                    className="w-full py-2 rounded-xl bg-[#C89900] text-white font-semibold shadow-md shine-button-hover"
                  >
                    Apply Filters
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

function FundCardComponent({ fund }: { fund: FundItem }) {
  const dispatch = useDispatch();

  const schemeDataItems = {
    scheme_code: fund.scheme_code,
    scheme_name: fund.scheme_name,
    amc_code: fund.amc_code,
    sip_allowed: fund.sip_allowed,
    lump_allowed: fund.lump_allowed,
    minimum_sip_invest: fund.minimum_sip_invest,
    minimum_investment: fund.minimum_investment,
  };

  return (
    <div
      className={`relative w-76 rounded-2xl p-4 bg-[#FFFFFF26] overflow-hidden`}
    >
      <Link
        href={`/mutual-fund/${fund.url}/mso${fund.id}`}
        className="text-sm font-medium h-10 flex items-center gap-2 mb-4"
      >
        <Image
          src={`${ROOT_URL}img/AMCsLogo/${fund.amc_code}.png`}
          alt={fund?.scheme_name || "Fund Icon"}
          width={28}
          height={28}
          className="w-7 h-7 overflow-hidden shrink-0 border border-[#E9E9EB] rounded-full"
        />

        <p className="line-clamp-2 leading-1.5 text-white">
          {fund.scheme_name}
        </p>
      </Link>

      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-[#C6C6C6] text-xs">Returns (1M)</p>
          <p
            className={`font-semibold text-2xs leading-4 mt-1 ${fund?.six_month_abs && fund?.six_month_abs > 0 ? "text-[#00F12C]" : fund?.six_month_abs === 0 ? "text-white" : "text-[#ff0000]"}`}
          >
            {fund.six_month_abs ?? "-"}%
          </p>
        </div>

        <div>
          <p className="text-[#C6C6C6] text-xs">Returns (1Y)</p>
          <p
            className={`font-semibold text-2xs leading-4 mt-1 ${fund?.one_year_abs && fund?.one_year_abs > 0 ? "text-[#00F12C]" : fund?.one_year_abs === 0 ? "text-white" : "text-[#ff0000]"}`}
          >
            {fund.one_year_abs ?? "-"}%
          </p>
        </div>

        <div>
          <p className="text-[#C6C6C6] text-xs">Returns (3Y)</p>
          <p
            className={`font-semibold text-2xs leading-4 mt-1 ${fund?.three_year && fund?.three_year > 0 ? "text-[#00F12C]" : fund?.three_year === 0 ? "text-white" : "text-[#ff0000]"}`}
          >
            {fund.three_year ?? "-"}%
          </p>
        </div>

        <button
          onClick={() => dispatch(openInvestmentModal(schemeDataItems))}
          aria-label="Investment Popup Button"
          className="w-6 h-6 flex items-center justify-center border-2 border-white rounded-full text-white"
        >
          <Icon.rightArrow className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
